import { useState, useEffect, useCallback } from "react";
import { z } from "zod";
import { useDioptraFetch } from "./use-dioptra-fetch";
import { getEnv } from "../utils/envManager";
import { IcertisConnectedUserSchema, type IcertisConnectedUser } from "@common/schemas/icertis";

export type IcertisIntegrationStatus = "active" | "expired" | "mismatch" | "not found";

export interface IcertisInstanceMismatch {
    orgInstanceHost: string;
    connectedInstanceHost: string;
}

const DEFAULT_INTEGRATION_STATUS: IcertisIntegrationStatus = "not found";

const IcertisIntegrationResponseSchema = z.object({
    status: z.enum(["active", "expired", "mismatch"]),
    integration: z.object({
        integrationConfig: z.object({
            icmBaseUrl: z.string().nullish(),
        }).nullish(),
    }).nullish(),
    mismatch: z.object({
        orgInstanceHost: z.string(),
        connectedInstanceHost: z.string(),
    }).nullish(),
    connectedUser: IcertisConnectedUserSchema.nullish(),
}).refine(
    (response) => response.status !== "mismatch" || response.mismatch != null,
    { message: "mismatch status requires the mismatch payload", path: ["mismatch"] },
);

const IcertisIntegrationConfigResponseSchema = z.object({
    config: z.object({
        icmBaseUrl: z.string().nullish(),
    }).nullish(),
});

function isNotFoundError(error: unknown): boolean {
    return (error as { status?: number })?.status === 404;
}

export const useIcertisIntegration = () => {
    const [hasIntegration, setHasIntegration] = useState<boolean>(false);
    const [status, setStatus] = useState<IcertisIntegrationStatus>(DEFAULT_INTEGRATION_STATUS);
    const [icmBaseUrl, setIcmBaseUrl] = useState<string | undefined>(undefined);
    const [mismatch, setMismatch] = useState<IcertisInstanceMismatch | null>(null);
    const [connectedUser, setConnectedUser] = useState<IcertisConnectedUser | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { dioptraFetch } = useDioptraFetch();
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    const checkConfigFallback = useCallback(async () => {
        const configResponse = IcertisIntegrationConfigResponseSchema.parse(
            await dioptraFetch(`${getEnv("WEBAPP_API_BASE_URL")}/api/user-integrations/icertis/config`)
        );

        if (configResponse.config?.icmBaseUrl) {
            setHasIntegration(true);
            setStatus("expired");
            setIcmBaseUrl(configResponse.config.icmBaseUrl);
        } else {
            setHasIntegration(false);
            setStatus(DEFAULT_INTEGRATION_STATUS);
            setIcmBaseUrl(undefined);
        }
        setMismatch(null);
        setConnectedUser(null);
    }, [dioptraFetch]);

    const checkIntegration = useCallback(async () => {
        setIsLoading(true);
        try {
            let response;
            try {
                response = IcertisIntegrationResponseSchema.parse(
                    await dioptraFetch(`${getEnv("WEBAPP_API_BASE_URL")}/api/user-integrations/icertis`)
                );
            } catch (error) {
                if (!isNotFoundError(error)) throw error;
                await checkConfigFallback();
                return;
            }

            setHasIntegration(true);
            setStatus(response.status);
            setIcmBaseUrl(response.integration?.integrationConfig?.icmBaseUrl ?? undefined);
            setMismatch(response.mismatch ?? null);
            setConnectedUser(response.connectedUser ?? null);
        } catch (error) {
            console.error("Failed to check Icertis integration status", error);
        } finally {
            setIsLoading(false);
        }
    }, [dioptraFetch, checkConfigFallback]);

    useEffect(() => {
        checkIntegration();
    }, [checkIntegration, refreshTrigger]);

    const refresh = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    return { hasIntegration, status, icmBaseUrl, mismatch, connectedUser, isLoading, refresh };
};

